import { useDispatch } from "react-redux";
import {
  addAttachmentToTake,
  removeAttachmentFromTake,
} from "../../app/slices/TrainingSlice";
import useAttachmentHelper from "../../hooks/AttachmentHelper.hook";
import usePropHelper from "../../hooks/PropHelper";
import { ActorState } from "../../models/ActorState";

/**
 * The props attached to an actor in the selected take, and the edits on them.
 * Shared by the attachment list (`ActorStatePropInfoField`) and the sentence rows
 * of the actor settings popup (`ActorStateAttachmentRows`).
 */
export default function useActorStateAttachments(actorState: ActorState) {
  const dispatch = useDispatch();
  const { getAllAttachableProps, getPropsAttachedToActor } =
    useAttachmentHelper();
  const { getUsedPropByInstanceId } = usePropHelper();

  const attachedProps = getPropsAttachedToActor(actorState.actorId);
  const attachableProps = getAllAttachableProps();
  const selectedPropIds = attachedProps.map(
    (attachedProp) => attachedProp.propPreset.id
  );

  const getDefaultTag = (propId: string) => {
    const usedProp = getUsedPropByInstanceId(propId);
    return usedProp.metadata.attachmentTags?.[0]?.tag ?? "";
  };

  /** Adds (or overwrites) the attachment of a prop to this actor. */
  const setAttachment = (propId: string, tag: string, playerToProp?: boolean) => {
    dispatch(
      addAttachmentToTake({
        actorId: actorState.actorId,
        propId,
        tag,
        playerToProp,
      })
    );
  };

  const addAttachment = (propId: string, playerToProp?: boolean) => {
    if (!propId) return;
    setAttachment(propId, getDefaultTag(propId), playerToProp);
  };

  const removeAttachment = (propId: string) => {
    dispatch(
      removeAttachmentFromTake({
        actorId: actorState.actorId,
        propId,
      })
    );
  };

  const changeAttachmentProp = (
    currentPropId: string,
    nextPropId: string,
    playerToProp: boolean
  ) => {
    if (!nextPropId || currentPropId === nextPropId) return;
    removeAttachment(currentPropId);
    addAttachment(nextPropId, playerToProp);
  };

  /** Attachable props that are free to pick, plus `currentPropId` itself. */
  const getAttachmentOptions = (currentPropId?: string) =>
    attachableProps.filter(
      (prop) => prop.id === currentPropId || !selectedPropIds.includes(prop.id)
    );

  return {
    attachedProps,
    selectedPropIds,
    addAttachment,
    removeAttachment,
    changeAttachmentProp,
    updateAttachmentTag: setAttachment,
    updateAttachmentType: setAttachment,
    getAttachmentOptions,
  } as const;
}
